import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, CalendarPlus, TrendingUp, List, Settings } from 'lucide-react';

// Dark Theme Color System
export const colors = {
  bg: {
    primary: '#0F1115',
    card: '#1A1D23',
    cardAlt: '#22252E',
    cardHover: '#2A2E38',
  },
  text: {
    primary: '#FFFFFF',
    secondary: '#A1A1AA',
    muted: '#71717A',
  },
  accent: {
    primary: '#FF7A00',
    secondary: '#FF8C42',
    gradient: 'linear-gradient(135deg, #FF7A00 0%, #FF8C42 100%)',
  },
  status: {
    success: '#22C55E',
    warning: '#F59E0B',
    error: '#EF4444',
  }
};

// Bottom Navigation Component
export const BottomNav = ({ activeTab }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const tabs = [
    { id: 'home', label: '홈', icon: Home, path: '/app/home' },
    { id: 'book', label: '예약', icon: CalendarPlus, path: '/app/book' },
    { id: 'score', label: '점수', icon: TrendingUp, path: '/app/score' },
    { id: 'my-classes', label: '내 수업', icon: List, path: '/app/my-classes' },
    { id: 'settings', label: '설정', icon: Settings, path: '/app/settings' },
  ];

  const isActive = (tab) => {
    if (activeTab) return activeTab === tab.id;
    return location.pathname === tab.path;
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-[#1A1D23] border-t border-[#22252E] safe-area-bottom">
      <div className="flex justify-around items-center h-16 max-w-lg mx-auto">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const active = isActive(tab);
          
          return (
            <button
              key={tab.id}
              onClick={() => navigate(tab.path)}
              className={`flex flex-col items-center justify-center flex-1 py-2 transition-all duration-200 active:scale-95 ${
                active ? 'text-[#FF7A00]' : 'text-[#52525B] hover:text-[#71717A]'
              }`}
            >
              <Icon className={`h-5 w-5 ${active ? 'stroke-[2]' : 'stroke-[1.5]'}`} />
              <span className={`text-[10px] mt-1 ${active ? 'font-medium' : 'font-normal'}`}>
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

// Dark Card Component
export const DarkCard = ({ children, className = '', onClick, hover = false }) => {
  return (
    <div
      onClick={onClick}
      className={`
        bg-[#1A1D23] rounded-2xl p-5
        ${hover ? 'cursor-pointer hover:bg-[#22252E] active:scale-[0.98] transition-all duration-200' : ''}
        ${className}
      `}
    >
      {children}
    </div>
  );
};

// Gradient Text
export const GradientText = ({ children, className = '' }) => (
  <span 
    className={`bg-gradient-to-r from-[#FF7A00] to-[#FF8C42] bg-clip-text text-transparent ${className}`}
  >
    {children}
  </span>
);

// Primary Button
export const PrimaryButton = ({ 
  children, 
  onClick, 
  disabled = false, 
  className = '',
  size = 'md',
  fullWidth = false,
  ...props 
}) => {
  const sizeClasses = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg',
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        bg-gradient-to-r from-[#FF7A00] to-[#FF8C42] 
        text-white font-semibold rounded-xl
        hover:brightness-110 active:scale-[0.98] 
        disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100
        transition-all duration-200
        ${sizeClasses[size]}
        ${fullWidth ? 'w-full' : ''}
        ${className}
      `}
      {...props}
    >
      {children}
    </button>
  );
};

// Secondary Button
export const SecondaryButton = ({ 
  children, 
  onClick, 
  disabled = false, 
  className = '',
  size = 'md',
  ...props 
}) => {
  const sizeClasses = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg',
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        bg-[#22252E] border border-[#3A3F4A] 
        text-white font-medium rounded-xl
        hover:bg-[#2A2E38] active:scale-[0.98]
        disabled:opacity-50 disabled:cursor-not-allowed
        transition-all duration-200
        ${sizeClasses[size]}
        ${className}
      `}
      {...props}
    >
      {children}
    </button>
  );
};

// Section Title
export const SectionTitle = ({ children, icon: Icon, action, className = '' }) => (
  <div className={`flex items-center justify-between mb-4 ${className}`}>
    <div className="flex items-center gap-2">
      {Icon && <Icon className="h-5 w-5 text-[#A1A1AA]" />}
      <h2 className="text-white font-semibold">{children}</h2>
    </div>
    {action}
  </div>
);

// Badge Component
export const Badge = ({ children, variant = 'default', className = '' }) => {
  const variants = {
    default: 'bg-[#22252E] text-[#A1A1AA]',
    primary: 'bg-[#FF7A00]/20 text-[#FF8C42]',
    success: 'bg-[#22C55E]/20 text-[#22C55E]',
    warning: 'bg-[#F59E0B]/20 text-[#F59E0B]',
    error: 'bg-[#EF4444]/20 text-[#EF4444]',
  };

  return (
    <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${variants[variant]} ${className}`}>
      {children}
    </span>
  );
};

// Stat Card
export const StatCard = ({ label, value, unit, icon: Icon, trend, className = '' }) => (
  <div className={`bg-[#22252E] rounded-xl p-4 ${className}`}>
    <div className="flex items-center justify-between mb-2">
      <span className="text-[#71717A] text-sm">{label}</span>
      {Icon && <Icon className="h-4 w-4 text-[#A1A1AA]" />}
    </div>
    <div className="flex items-baseline gap-1">
      <span className="text-2xl font-bold text-white">{value}</span>
      {unit && <span className="text-[#A1A1AA] text-sm">{unit}</span>}
    </div>
    {trend && (
      <div className={`mt-1 text-xs ${trend > 0 ? 'text-[#22C55E]' : trend < 0 ? 'text-[#EF4444]' : 'text-[#A1A1AA]'}`}>
        {trend > 0 ? '+' : ''}{trend}%
      </div>
    )}
  </div>
);

export default {
  colors,
  BottomNav,
  DarkCard,
  GradientText,
  PrimaryButton,
  SecondaryButton,
  SectionTitle,
  Badge,
  StatCard,
};
